import React, { useEffect, useState } from "react";
import { Ticket, Gift, Clock, Menu, X, ExternalLink } from "lucide-react";
import { fmtCountdown } from "../lib/helpers";
import { EXPERIENCES, HOME_URL, LOGO_SRC } from "../lib/brand";

function Budget({ state, now }) {
  const windowEnd = state?.window_end ? new Date(state.window_end).getTime() : null;
  const remainingMs = windowEnd ? windowEnd - now : 0;
  const full = !windowEnd || remainingMs <= 0 || state?.ordinary_remaining >= 3;
  return (
    <div className="flex items-center gap-1.5" data-testid="header-budget">
      <div className="luma-card !rounded-xl !shadow-none px-2.5 py-1.5 flex items-center gap-1.5" title="Aperture ordinarie disponibili" data-testid="ordinary-count">
        <Ticket size={16} className="text-[#16b8d6]" />
        <span className="font-display font-bold text-[15px]">{state?.ordinary_remaining ?? "–"}</span>
      </div>
      <div className="luma-card !rounded-xl !shadow-none px-2.5 py-1.5 flex items-center gap-1.5" title="Bustine bonus disponibili" data-testid="bonus-count">
        <Gift size={16} className="text-[#c64bb4]" />
        <span className="font-display font-bold text-[15px]">{state?.bonus_balance ?? "–"}</span>
      </div>
      <div className="luma-card !rounded-xl !shadow-none px-2.5 py-1.5 flex items-center gap-1.5 font-mono" title="Ripristino aperture" data-testid="reset-countdown">
        <Clock size={15} className="text-[#7433c1]" />
        <span className="font-bold text-[13px] tabular-nums">{full ? "Pronto" : fmtCountdown(remainingMs)}</span>
      </div>
    </div>
  );
}

function NavLinks({ onNavigate, stacked }) {
  return (
    <>
      {EXPERIENCES.map((e) =>
        e.current ? (
          <span
            key={e.key}
            aria-current="page"
            data-testid="nav-current-sbustaop"
            className={`font-bold text-sm text-[#7433c1] bg-[#f5eeff] rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 ${stacked ? "justify-between" : ""}`}
          >
            {e.label} <span className="text-[10px] font-extrabold uppercase tracking-wide bg-[#7433c1] text-white rounded-full px-1.5 py-0.5">Sei qui</span>
          </span>
        ) : (
          <a
            key={e.key}
            href={e.url}
            target={e.sameTab ? "_self" : "_blank"}
            rel={e.sameTab ? undefined : "noopener noreferrer"}
            onClick={onNavigate}
            data-testid={`nav-${e.key}`}
            className={`text-[#506780] hover:text-[#14233d] font-semibold text-sm inline-flex items-center gap-1.5 ${stacked ? "min-h-[44px]" : ""}`}
          >
            {e.label} <ExternalLink size={13} className="opacity-60" />
          </a>
        )
      )}
    </>
  );
}

export default function Header({ state, onTick }) {
  const [now, setNow] = useState(Date.now());
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const windowEnd = state?.window_end ? new Date(state.window_end).getTime() : null;
  const remainingMs = windowEnd ? windowEnd - now : 0;
  useEffect(() => {
    if (windowEnd && remainingMs <= 0) onTick?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingMs <= 0]);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#d0e4f3]">
      <div className="max-w-md sm:max-w-2xl lg:max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between gap-3 py-2 min-h-[64px]">
          {/* brand → home */}
          <a href={HOME_URL} target="_blank" rel="noopener noreferrer" data-testid="brand-home-link" className="flex items-center gap-2.5 shrink-0">
            <img src={LOGO_SRC} alt="Lumacofono" className="w-11 h-11 sm:w-[54px] sm:h-[54px] object-contain" />
            <span className="font-display font-extrabold text-[20px] sm:text-[22px] text-[#14233d] leading-none tracking-tight">Lumacofono</span>
          </a>

          <div className="flex items-center gap-3">
            <nav className="hidden lg:flex items-center gap-5" aria-label="Esperienze Lumacofono">
              <NavLinks />
            </nav>
            <div className="hidden sm:block">
              <Budget state={state} now={now} />
            </div>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Apri il menu"
              aria-expanded={menuOpen}
              data-testid="menu-button"
              className="lg:hidden pill-soft w-11 h-11 flex items-center justify-center"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* mobile budget row */}
        <div className="sm:hidden pb-2 flex justify-center">
          <Budget state={state} now={now} />
        </div>

        {/* mobile nav dropdown */}
        {menuOpen && (
          <nav className="lg:hidden pb-3 flex flex-col gap-1 border-t border-[#d0e4f3] pt-2" aria-label="Esperienze Lumacofono" data-testid="mobile-nav">
            <NavLinks stacked onNavigate={() => setMenuOpen(false)} />
          </nav>
        )}
      </div>
    </header>
  );
}
